import React from 'react';
import styled from 'styled-components';
import { compose, space, variant } from 'styled-system';

import type {
  FontWeightProps,
  TextAlignProps,
  TextColorProps,
  ThemeHeadingSizes,
} from '@rover/kibble/styles';
import { DSTokenMap, fontWeight, textAlign, textColor } from '@rover/kibble/styles';

import Box from '../Box';
import type { SpacingSystemProps } from '../styledSystemPropTypes';

export type HeadingTags = 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type Props = SpacingSystemProps &
  TextAlignProps &
  TextColorProps &
  FontWeightProps & {
    children: any;
    as?: HeadingTags;
    responsive?: boolean;
    size?: ThemeHeadingSizes;
    id?: string;
  };
export const DEFAULT_TAGS_FOR_SIZES: Record<ThemeHeadingSizes, HeadingTags> = {
  '100': 'h5',
  '200': 'h4',
  '300': 'h3',
  '400': 'h2',
  '500': 'h1',
  '600': 'div',
};
export const getTag = (size: ThemeHeadingSizes, as?: HeadingTags): HeadingTags => {
  if (as) {
    return as;
  }

  return DEFAULT_TAGS_FOR_SIZES[size];
};
const StyledHeading = styled(Box)<Props>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
  },
  (props) =>
    variant({
      prop: 'size',
      variants: {
        100: {
          fontFamily: `${DSTokenMap.HEADING_100_FONT_FAMILY} !important`,
          fontSize: DSTokenMap.HEADING_100_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_100_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_100_MARGIN_BOTTOM,
        },
        200: {
          fontFamily: `${DSTokenMap.HEADING_200_FONT_FAMILY} !important`,
          fontSize: DSTokenMap.HEADING_200_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_200_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_200_MARGIN_BOTTOM,
        },
        300: {
          fontFamily: `${DSTokenMap.HEADING_300_FONT_FAMILY} !important`,
          fontSize: DSTokenMap.HEADING_300_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_300_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_300_MARGIN_BOTTOM,
        },
        400: {
          fontFamily: `${DSTokenMap.HEADING_400_FONT_FAMILY} !important`,
          fontSize: props.responsive
            ? [DSTokenMap.HEADING_400_FONT_SIZE, DSTokenMap.HEADING_RESPONSIVE_400_FONT_SIZE]
            : DSTokenMap.HEADING_400_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_400_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_400_MARGIN_BOTTOM,
        },
        500: {
          fontFamily: `${DSTokenMap.HEADING_500_FONT_FAMILY} !important`,
          fontSize: props.responsive
            ? [DSTokenMap.HEADING_500_FONT_SIZE, DSTokenMap.HEADING_RESPONSIVE_500_FONT_SIZE]
            : DSTokenMap.HEADING_500_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_500_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_500_MARGIN_BOTTOM,
        },
        600: {
          fontFamily: `${DSTokenMap.HEADING_600_FONT_FAMILY} !important`,
          fontSize: props.responsive
            ? [DSTokenMap.HEADING_600_FONT_SIZE, DSTokenMap.HEADING_RESPONSIVE_600_FONT_SIZE]
            : DSTokenMap.HEADING_600_FONT_SIZE,
          lineHeight: DSTokenMap.HEADING_600_LINE_HEIGHT,
          marginBottom: DSTokenMap.HEADING_600_MARGIN_BOTTOM,
        },
      },
    })(props),
  compose(textAlign, textColor, fontWeight, space) // Apply last to avoid being overwritten by resets and variant styles
);

function Heading(props: Props): JSX.Element {
  return <StyledHeading as={getTag(props.size as ThemeHeadingSizes, props.as)} {...props} />;
}

Heading.defaultProps = {
  size: '200',
  responsive: 'true',
};

export default Heading;



// WEBPACK FOOTER //
// ../kibble/components/Heading/Heading.tsx